"use client";

import React, { Fragment } from "react";
import { Popover, Transition } from "@headlessui/react";
import Slider from "rc-slider";
import ButtonThird from "@/shared/ButtonThird";
import ButtonPrimary from "@/shared/ButtonPrimary";
import convertNumbThousand from "@/utils/convertNumbThousand";
import { useAppDispatch, useAppSelector } from "@/app/lib/hooks";
import { setFilter } from "@/app/lib/reducers/toursReducer";
import { useTranslation } from 'react-i18next';

const PriceRangeFilter: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("guydeez");
  const { minPrice, maxPrice } = useAppSelector((state) => state.tours.filters);
  const selectedCurrency = useAppSelector((state) => state.currencies.selectedCurrency);
  const symbol = useAppSelector((state) =>
    state.currencies.currencies.find((c) => c.code === selectedCurrency)?.symbol || "€"
  );

  const handleChange = (range: number[]) => {
    dispatch(setFilter("minPrice", range[0]));
    dispatch(setFilter("maxPrice", range[1]));
  };

  const clear = () => {
    dispatch(setFilter("minPrice", 0));
    dispatch(setFilter("maxPrice", 1000));
  };

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <Popover.Button
            className={`flex items-center justify-center px-4 py-2 text-sm rounded-full border ${
              open ? "!border-primary-500" : "border-neutral-300 dark:border-neutral-700"
            } hover:border-neutral-400 dark:hover:border-neutral-600 focus:outline-none`}
          >
            <span>{`${symbol}${convertNumbThousand(minPrice)} - ${symbol}${convertNumbThousand(maxPrice)}`}</span>
            <i className="las la-angle-down ml-2" />
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute z-50 w-screen max-w-sm px-4 mt-3 sm:px-0 lg:max-w-md">
              <div className="rounded-2xl shadow-xl bg-white dark:bg-neutral-900 border dark:border-neutral-700 p-5 space-y-5">
                <span className="font-medium">{t('price_per_day_per_person')}</span>
                <Slider
                  range
                  min={0}
                  max={2000}
                  value={[minPrice, maxPrice]}
                  allowCross={false}
                  onChange={(val) => handleChange(val as number[])}
                />
                <div className="flex justify-between gap-4">
                  <input
                    type="number"
                    value={minPrice}
                    onChange={(e) => handleChange([+e.target.value, maxPrice])}
                    className="block w-full pl-7 pr-3 sm:text-sm border rounded-full"
                  />
                  <input
                    type="number"
                    value={maxPrice}
                    onChange={(e) => handleChange([minPrice, +e.target.value])}
                    className="block w-full pl-7 pr-3 sm:text-sm border rounded-full"
                  />
                </div>
                <div className="flex justify-between pt-4 border-t dark:border-neutral-800">
                  <ButtonThird onClick={() => { clear(); close(); }}>{t('clear')}</ButtonThird>
                  <ButtonPrimary onClick={close}>{t('apply')}</ButtonPrimary>
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default PriceRangeFilter;
